/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/await-thenable */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { LoadingOverlay, Text } from "@mantine/core";
import { ITableProps, kaReducer } from "ka-table";
import {
  hideLoading, loadData, showLoading, updateData, updatePageIndex, updatePagesCount,
} from "ka-table/actionCreators";
import { ActionType, SortDirection, SortingMode } from "ka-table/enums";
import { DispatchFunc } from "ka-table/types";
import { capitalize, isEqual } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useTraitsAndMetrics, useViews, useWorkspaceMembers } from "../../../hooks/QueryHooks";
import { getWorkspaceTrendsAgaintQuery } from "../../../services/workspace/workspaceEndpoints";
import KaTable, { IColumn, ISortBy } from "../../../shared/table/KaTable";
import useViewStore from "../../../store/view/viewStore";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import parseTableData from "../../../utils/parseTable";
import AccountControls from "./components/AccountControls";
import AccountHeader from "./components/AccountHeader";

const AccountsPage = () => {
  const { viewId } = useParams<{ viewId: string }>();
  const location = useLocation();
  const { updateView, setView } = useViewStore((state: any) => ({
    updateView: state.updateView,
    setView: state.setView,
  }));

  // Making current view transient as explained in zustand docs
  const storedView = useRef<any>(useViewStore.getState().view);

  const currentView = storedView.current;

  useEffect(() => {
    useViewStore.subscribe(
      (state: any) => { storedView.current = state.view; },
    );
  }, []);

  const workspace = useWorkspaceStore((state) => state.workspace);

  const { data: workspaceMembers, isLoading, isSuccess }: any = useWorkspaceMembers({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
    retry: 2,
  });

  const traitsAndMetrics: any = useTraitsAndMetrics({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const views: any = useViews({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  useEffect(() => {
    if (viewId && views.isSuccess && views?.data?.length && !location.pathname.includes("new")) {
      setView(views?.data?.find((view: any) => view.id === viewId));
    }
  }, [viewId, views.isSuccess]);

  const traits: any = currentView?.entity_relation === "accounts" ? traitsAndMetrics?.data?.accountTraits : traitsAndMetrics?.data?.contactTraits;

  const metrics: any = currentView?.entity_relation === "accounts" ? traitsAndMetrics?.data?.accountMetrics : traitsAndMetrics?.data?.contactMetrics;

  const tableRef = useRef<any>(null);
  const accountControlsRef = useRef<any>(null);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);

  const [tableProps, changeTableProps] = useState<ITableProps>({
    columns: [],
    columnResizing: true,
    paging: {
      enabled: true,
      pageIndex: 0,
      pagesCount: 1,
      pageSize: 15,
    },
    columnReordering: true,
    rowKeyField: "randomId",
    height: (window.innerHeight - 360) > 300 ? (window.innerHeight - 360) : 300,
    singleAction: loadData(),
    sortingMode: SortingMode.SingleRemote,
    loading: {
      enabled: true,
      text: "Loading",
    },
  });

  const onSortChange = useCallback((payload: { field: string, order: string, type?: string }) => {
    const { field, order } = payload;
    updateView({
      sort_fields: {
        field,
        order,
        type: { ...traits?.obj, ...metrics?.obj }[field].attribute_type === "metric_traits" ? "changeval" : "property",
      },
    });
  }, [traits?.obj, metrics?.obj, currentView]);

  const kaDispatch: DispatchFunc = (action: any) => {
    changeTableProps((prevState: ITableProps) => kaReducer(prevState, action));
    const sortColumn: any = tableProps.columns.find((c) => c.sortDirection);
    if (action.type === ActionType.UpdateSortDirection) {
      // change sort direction
      let newSortDirection = SortDirection.Ascend;
      if (action.columnKey === sortColumn?.key) {
        newSortDirection = sortColumn?.sortDirection === SortDirection.Ascend ? SortDirection.Descend : SortDirection.Ascend;
      }
      if (tableProps.sortingMode === SortingMode.SingleRemote) {
        onSortChange({
          field: action.columnKey,
          order: newSortDirection === SortDirection.Ascend ? "asc" : "desc",
          type: undefined,
        });
      }
    }
  };

  const fetchData = async (page: number, sortedBy: ISortBy, signal: any) => {
    if (workspace?.id && currentView?.id) {
      setIsDataLoading(true);
      kaDispatch(showLoading());
      try {
        const response: any = await getWorkspaceTrendsAgaintQuery(
          {
            workspaceId: workspace?.id,
            entityType: currentView?.entity_relation,
            signal,
            query: {
              include_count: true,
              page: page + 1,
              per_page: 15,
              filter: {
                ...currentView?.filters,
                predicates: currentView?.filters?.predicates?.filter((fil: any) => fil?.predicates?.filter((item: any) => item.field !== "").length > 0).map((fil: any) => ({
                  ...fil,
                  predicates: fil?.predicates?.filter((item: any) => item.field !== ""),
                })),
              },
              selected_fields: currentView?.selected_columns || [],
              sort: {
                field: sortedBy?.key || currentView?.sort_fields?.field,
                order: sortedBy?.direction === SortDirection.Ascend ? "asc" : "desc",
                type: sortedBy?.type || ({ ...traits.obj, ...metrics.obj }[sortedBy?.key]?.attribute_type === "metric_traits" ? "changeval" : "property"),
              },
              metrics: currentView?.metrics,
              time_window: currentView?.metrics_time_frame,
              time_zone: workspace?.workspace_time_zone,
            },
          },
        );
        if (response?.data) {
          setTotal(response.total_results);
          const list = parseTableData({ data: response.data, memberData: workspaceMembers?.users });
          kaDispatch(updateData(list));
          if (response.total_results < ((tableProps?.paging?.pageIndex || 0) * (tableProps?.paging?.pageSize || 1))) {
            kaDispatch(updatePageIndex(0));
          }
          kaDispatch(updatePagesCount(Math.ceil(response.total_results / (tableProps?.paging?.pageSize ?? 1)) || 0));
          kaDispatch(hideLoading());
          setIsDataLoading(false);
        } else {
          setTotal(0);
          setNoResults(true);
          kaDispatch(updatePageIndex(0));
          kaDispatch(updateData([]));
          kaDispatch(updatePagesCount(1));
          kaDispatch(hideLoading());
          setIsDataLoading(false);
        }
      } catch (err: any) {
        if (err?.message === "Component unmounted or effect re-ran") {
          return;
        }
        setTotal(0);
        setNoResults(true);
        kaDispatch(updatePageIndex(0));
        kaDispatch(updateData([]));
        kaDispatch(updatePagesCount(1));
        kaDispatch(hideLoading());
        setIsDataLoading(false);
      }
    }
  };

  useEffect(() => {
    document.title = `${currentView?.view_name ? `${capitalize(currentView?.view_name)} | ` : ""
      }View | Hyperengage`;
  }, [currentView?.view_name]);

  // Define updateTableHeight function before using it
  const updateTableHeight = () => {
    const controlsHeight = accountControlsRef.current?.offsetHeight || 0;
    const headerOffset = 100;
    const dynamicHeight = (window.innerHeight - headerOffset - controlsHeight) > 400 ? (window.innerHeight - headerOffset - controlsHeight) : 400;

    // Properly update table props by spreading existing props
    changeTableProps((prevProps) => ({
      ...prevProps,
      height: dynamicHeight,
    }));

    // Only refresh the sorting if needed
    if (tableRef.current && tableProps.sort?.length) {
      // Get the current sort info
      const sortColumn = tableProps.columns.find((c) => c.sortDirection);
      if (sortColumn) {
        // Create a cancel token for the request
        const controller = new AbortController();
        const { signal } = controller;

        // Refresh the table data with current sort settings
        fetchData(
          tableProps.paging?.pageIndex || 0,
          {
            key: sortColumn.key,
            direction: sortColumn.sortDirection || SortDirection.Ascend,
            // Use type from columnDefs object if exists, or default to undefined
            type: (sortColumn as any).sortType
          },
          signal
        );
      }
    }
  };

  // ResizeObserver to monitor AccountControls height changes
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateTableHeight();
    });

    if (accountControlsRef.current) {
      resizeObserver.observe(accountControlsRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [traits]); // Re-run when traits change as this might affect controls layout

  // Handle window resize events
  useEffect(() => {
    const handleResize = () => {
      updateTableHeight();
    };

    window.addEventListener('resize', handleResize);
    // Initial calculation
    updateTableHeight();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /* ---------- rebuild columns when view / traits / metrics change ---------- */
  useEffect(() => {
    if (!isSuccess || !traits?.obj || !metrics?.obj) return;
    if (!(currentView?.selected_columns?.length || currentView?.metrics?.length)) return;

    const constructedCols: IColumn[] = [...(currentView?.selected_columns || []), ...(currentView?.metrics || []), "related_accounts"].reduce((arr: any, item: any) => {
      if (!{ ...traits.obj, ...metrics?.obj }[item] || { ...traits?.obj, ...metrics?.obj }[item]?.exclude_display) {
        // special column for related_accounts when entity is users
        if (item === "related_accounts" && currentView?.entity_relation === "users") {
          arr.push({
            key: "related_accounts",
            title: "Related Accounts",
            isResizable: true,
            colGroup: { style: { minWidth: 50, width: 200 } },
            isSortable: true,
            dataType: "object",
            attributeType: "trait",
          });
        }
        return arr;
      }
      const def = { ...traits.obj, ...metrics?.obj }[item];
      const col: any = {
        key: def.name,
        dataType: !item.includes("metric") ? def.data_type : "object",
        title: def.display_name,
        isResizable: true,
        colGroup: { style: { minWidth: 50, width: 200 } },
        isSortable: true,
        attributeType: def.attribute_type,
      };
      if (currentView?.sort_fields?.field === item) {
        col.sortType = currentView?.sort_fields?.type;
        col.sortDirection = currentView?.sort_fields?.order === "asc" ? SortDirection.Ascend : SortDirection.Descend;
      }
      if (item === "accounts.name" || item === "users.name") {
        arr.unshift(col);
      } else {
        arr.push(col);
      }
      return arr;
    }, [] as IColumn[]);

    // only update if different to avoid recreation loop
    changeTableProps(prev => (isEqual(prev.columns, constructedCols) ? prev : { ...prev, columns: constructedCols }));
  }, [isSuccess, traits?.obj, metrics?.obj, currentView]);

  /* ---------- fetch data whenever columns, paging, or sort change ---------- */
  useEffect(() => {
    const src = axios.CancelToken.source();
    fetchData(
      tableProps?.paging?.pageIndex || 0,
      {
        key: currentView?.sort_fields?.field,
        direction: currentView?.sort_fields?.order === "asc" ? SortDirection.Ascend : SortDirection.Descend,
        type: currentView?.sort_fields?.type,
      },
      src,
    );
    return () => src.cancel("component unmount / dependency change");
  }, [currentView?.selected_columns, tableProps?.paging?.pageIndex, currentView?.sort_fields, currentView?.filters, currentView?.metrics, currentView?.time_window, currentView?.metrics_time_frame, currentView?.time_zone, currentView?.id]);

  return (
    <div className="overflow-auto relative h-full">
      {traits && (
        <div ref={accountControlsRef}>
          <AccountControls
            traits={traits}
            metrics={metrics}
            count={total}
            currentView={currentView}
          />
        </div>
      )}
      <LoadingOverlay loaderProps={{ size: "sm" }} c="main.5" visible={isLoading || isDataLoading || !traits?.obj || !metrics?.obj} />
      <KaTable
        tableProps={tableProps}
        changeTableProps={changeTableProps}
        ref={tableRef}
        totalData={total}
        kaDispatch={kaDispatch}
        noDataTitle={currentView?.entity_relation === "accounts" ? "No Accounts Available" : "No Contacts Available"}
        noDataMessage={"We couldn't find any data for the given filters"}
        itemType={currentView?.entity_relation === "accounts" ? "Accounts" : "Contacts"}
        rowKeyField="randomId"
      />
      <Text size="10px" c="text.4" className="p-5 py-0 italic">
        <a href="https://clearbit.com">
          Logos provided by
          {" "}
          <Text span c="main.6">
            Clearbit
          </Text>
        </a>
      </Text>
    </div>
  );
};

export default AccountsPage;
